/**
 * ImportJobStatus - Shows import job progress for a dataset
 *
 * Props:
 *   datasetId - The dataset ID to check for import jobs
 */
import React, { useState, useEffect, useRef } from 'react';
import { FiLoader, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { getImportJobs } from '../../../../../services/aiOperationsApi';

interface StatusConfig {
  icon: IconType;
  color: string;
  bg: string;
  border: string;
  label: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  pending: {
    icon: FiClock,
    color: 'text-yellow-500',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    label: 'Pending',
  },
  processing: {
    icon: FiLoader,
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    label: 'Processing',
  },
  completed: {
    icon: FiCheckCircle,
    color: 'text-green-500',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    label: 'Completed',
  },
  failed: {
    icon: FiXCircle,
    color: 'text-red-500',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    label: 'Failed',
  },
};

interface ImportJob {
  id: string;
  status: string;
  source?: string;
  file_format?: string;
  total_rows?: number;
  imported_rows?: number;
  completed_at?: string;
  error_message?: string;
}

interface ImportJobStatusProps {
  datasetId: string;
}

const ImportJobStatus: React.FC<ImportJobStatusProps> = ({ datasetId }) => {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJobs = async () => {
    try {
      const result = await getImportJobs({ dataset_id: datasetId, limit: 5 });
      setJobs(result.data || []);
    } catch {
      // Silently fail — import jobs are optional info
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  // Poll when there are active jobs
  useEffect(() => {
    const hasActive = jobs.some((j) => ['processing', 'pending'].includes(j.status));
    if (hasActive) {
      intervalRef.current = setInterval(fetchJobs, 5000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  if (loading || jobs.length === 0) return null;

  const recentJobs = jobs.filter(
    (j) =>
      j.status === 'processing' ||
      j.status === 'pending' ||
      j.status === 'failed' ||
      (j.status === 'completed' &&
        j.completed_at != null &&
        new Date(j.completed_at) > new Date(Date.now() - 3600000))
  );

  if (recentJobs.length === 0) return null;

  return (
    <div className="mb-8 space-y-3">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        Import Jobs
      </h3>
      {recentJobs.map((job) => {
        const config = STATUS_CONFIG[job.status] ?? STATUS_CONFIG.pending;
        const Icon = config.icon;
        const progress =
          (job.total_rows ?? 0) > 0
            ? Math.round(((job.imported_rows ?? 0) / (job.total_rows ?? 1)) * 100)
            : 0;

        return (
          <div key={job.id} className={`${config.bg} border ${config.border} rounded-lg p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon
                  className={`w-4 h-4 ${config.color} ${job.status === 'processing' ? 'animate-spin' : ''}`}
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {config.label}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {job.source} / {job.file_format}
                </span>
              </div>
              {(job.total_rows ?? 0) > 0 && (
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {job.imported_rows ?? 0} / {job.total_rows} rows
                </span>
              )}
            </div>

            {/* Progress bar */}
            {(['processing', 'completed'].includes(job.status)) &&
              (job.total_rows ?? 0) > 0 && (
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      job.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(100, progress)}%` }}
                  />
                </div>
              )}

            {/* Error message */}
            {job.status === 'failed' && job.error_message && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{job.error_message}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ImportJobStatus;
